import L from 'leaflet';
import polyline from '@mapbox/polyline';
import { THEME_COLORS } from '../constants/theme';

export const OSM_TILE_URL = process.env.REACT_APP_MAP_TILE_URL
  || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
export const OSM_ATTRIBUTION = process.env.REACT_APP_MAP_TILE_ATTRIBUTION
  || '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

export function buildFullRouteStops(allDaysData) {
  if (!allDaysData || allDaysData.length <= 1) return null;

  let flat = [];
  allDaysData.forEach((day) => {
    const dayStops = day.stops || [];
    if (flat.length === 0) flat = [...dayStops];
    else flat.push(...dayStops.slice(1));
  });
  return flat;
}

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[ch]));

/**
 * Places the traveler picked along the route (savedPois), for the day shown or
 * for every day when the full route is shown.
 *
 * @returns {Array<{ name, lat, lng, category, stopName }>}
 */
export function getRoadTripPlaces(roadTripData, { showFullRoute = false } = {}) {
  const allDays = roadTripData?.allDaysData || [];
  const dayIndex = roadTripData?.dayIndex ?? 0;
  const days = showFullRoute && allDays.length > 1
    ? allDays
    : [allDays.find((day, idx) => (day.dayIndex ?? idx) === dayIndex) || allDays[0]].filter(Boolean);

  return days.flatMap((day) => (day.stops || []).flatMap((stop) => (
    (stop.savedPois || [])
      .filter((poi) => poi?.lat != null && poi?.lng != null)
      .map((poi) => ({
        name: poi.name,
        lat: Number(poi.lat),
        lng: Number(poi.lng),
        category: poi.category || 'activity',
        stopName: stop.name,
      }))
  )));
}

const PLACE_MARKER_COLORS = {
  restaurant: THEME_COLORS.categories.restaurant.main,
  hotel: THEME_COLORS.categories.hotel.main,
  activity: THEME_COLORS.categories.activity.main,
};

export function hasRoadTripMapData(roadTripData) {
  if (!roadTripData) return false;

  const stopsWithCoords = (roadTripData.orderedStops || []).filter(
    (stop) => stop.lat != null && stop.lng != null
  );

  return stopsWithCoords.length >= 2 || Boolean(roadTripData.encodedPolyline);
}

export function scheduleMapLayoutUpdate(map, callback, { invalidate = true } = {}) {
  if (!map) return;

  map.whenReady(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (invalidate) {
          try {
            map.invalidateSize({ animate: false, pan: false });
          } catch {
            // Ignore benign browser resize observer timing issues.
          }
        }
        callback?.(map);
      });
    });
  });
}

export function renderRoadTripOnMap(map, roadTripData, options = {}) {
  const {
    showFullRoute = false,
    fullRouteStops = null,
    showAttribution = true,
    polylineWeight = 5,
    polylineOpacity = 0.85,
    fullRoutePolylineWeight = 4,
    fullRoutePolylineOpacity = 0.7,
    markerSize = 30,
    markerBorder = 3,
    markerFontSize = 13,
    fitPadding = [40, 40],
    bindPopups = true,
    autoFitBounds = true,
  } = options;

  const allDays = roadTripData?.allDaysData || [];
  const useFullRoute = showFullRoute && allDays.length > 1;

  const stops = useFullRoute && fullRouteStops?.length
    ? fullRouteStops
    : roadTripData?.orderedStops || [];

  if (stops.length === 0 && !roadTripData?.encodedPolyline) {
    return { bounds: null, stops: [] };
  }

  L.tileLayer(OSM_TILE_URL, {
    attribution: showAttribution ? OSM_ATTRIBUTION : '',
    maxZoom: 19,
  }).addTo(map);

  const bounds = L.latLngBounds([]);
  const firstStop = stops[0];
  if (firstStop?.lat != null && firstStop?.lng != null) {
    map.setView([firstStop.lat, firstStop.lng], 6);
  }

  if (useFullRoute) {
    allDays.forEach((day) => {
      if (!day.encodedPolyline) return;
      try {
        const decoded = polyline.decode(day.encodedPolyline);
        L.polyline(decoded, {
          color: THEME_COLORS.primary.main,
          weight: fullRoutePolylineWeight,
          opacity: fullRoutePolylineOpacity,
          smoothFactor: 1,
        }).addTo(map);
        decoded.forEach((pt) => bounds.extend(pt));
      } catch {
        // skip invalid polyline
      }
    });
  } else if (roadTripData?.encodedPolyline) {
    try {
      const decodedPath = polyline.decode(roadTripData.encodedPolyline);
      L.polyline(decodedPath, {
        color: THEME_COLORS.primary.main,
        weight: polylineWeight,
        opacity: polylineOpacity,
        smoothFactor: 1,
      }).addTo(map);
      decodedPath.forEach((pt) => bounds.extend(pt));
    } catch (e) {
      console.warn('Polyline decode error:', e);
    }
  }

  stops.forEach((stop, index) => {
    if (stop.lat == null || stop.lng == null) return;

    const latlng = [stop.lat, stop.lng];
    bounds.extend(latlng);

    const fillColor = index === 0
      ? THEME_COLORS.categories.transport.main
      : index === stops.length - 1
        ? THEME_COLORS.primary.main
        : THEME_COLORS.secondary.main;

    const icon = L.divIcon({
      className: 'road-trip-map-marker',
      html: `<div style="
        width: ${markerSize}px; height: ${markerSize}px; border-radius: 50%;
        background-color: ${fillColor}; color: #fff;
        display: flex; align-items: center; justify-content: center;
        font-weight: 700; font-size: ${markerFontSize}px; font-family: sans-serif;
        border: ${markerBorder}px solid #fff;
        box-shadow: 0 2px 6px rgba(0,0,0,0.35);
      ">${index + 1}</div>`,
      iconSize: [markerSize, markerSize],
      iconAnchor: [markerSize / 2, markerSize / 2],
      popupAnchor: [0, -(markerSize / 2 + 2)],
    });

    const marker = L.marker(latlng, { icon, title: stop.name });
    if (bindPopups) {
      marker.bindPopup(`<strong>${escapeHtml(stop.name)}</strong>`);
    }
    marker.addTo(map);
  });

  const places = getRoadTripPlaces(roadTripData, { showFullRoute: useFullRoute });
  places.forEach((place) => {
    const latlng = [place.lat, place.lng];
    bounds.extend(latlng);
    const size = Math.round(markerSize * 0.6);
    const icon = L.divIcon({
      className: 'road-trip-map-place',
      html: `<div style="
        width: ${size}px; height: ${size}px; border-radius: 50%;
        background-color: ${PLACE_MARKER_COLORS[place.category] || PLACE_MARKER_COLORS.activity};
        border: 2px solid #fff;
        box-shadow: 0 1px 4px rgba(0,0,0,0.35);
      "></div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -(size / 2 + 2)],
    });
    const marker = L.marker(latlng, { icon, title: place.name, zIndexOffset: -100 });
    if (bindPopups) {
      marker.bindPopup(`<strong>${escapeHtml(place.name)}</strong>`);
    }
    marker.addTo(map);
  });

  if (autoFitBounds && bounds.isValid()) {
    map.fitBounds(bounds, { padding: fitPadding, animate: false });
  }

  return { bounds, stops, places };
}
